import { css } from '@emotion/react'
import normalizeUrl from 'normalize-url'
import MagicUrl from 'quill-magic-url'
import 'quill-paste-smart'
import { useCallback, useEffect, useRef } from 'react'
import ReactQuill, { Quill } from 'react-quill'
import toastr from 'toastr'
import { connectField } from 'uniforms'
import cloudinaryTools from '../../../../util/cloudinaryTools'
import { UploadState } from '../../../../util/constants'
import { mediaSrc } from '../../misc/MediaPresets'

Quill.register('modules/magicUrl', MagicUrl)

const Link = Quill.import('formats/link')

class AbsoluteLink extends Link {
  static create(value) {
    const node = super.create(value)
    value = this.sanitize(value)
    try {
      value = normalizeUrl(value, { stripWWW: false })
    } catch (error) {
      console.error(error)
    }

    if (/(https?:\/\/)?(www\.)?civiclift.com/i.test(value)) {
      node.removeAttribute('target')
    }
    node.setAttribute('href', value)
    return node
  }
}

Quill.register(AbsoluteLink)

const RichTextEditor = ({
  onChange,
  onUploadingStateChange,
  toolbar,
  isRequired,
  label,
  id,
  formats,
  value: initialValue,
  placeholder,
}) => {
  const editor = useRef(null)

  const insertToEditor = useCallback(
    publicId => {
      // push image publicId to rich editor.
      const range = editor.current.getEditor().getSelection()
      editor.current.getEditor().insertEmbed(range.index, 'image', mediaSrc({ publicId }))
    },
    [editor],
  )

  const uploadFileToCloudinary = useCallback(
    async (file: File) => {
      onUploadingStateChange && onUploadingStateChange(UploadState.UPLOADING)

      await cloudinaryTools.upload(file, { folder: 'uploads' }, (error, response) => {
        const uploadState = error ? UploadState.ERROR : UploadState.SUCCESS
        onUploadingStateChange && onUploadingStateChange(uploadState)

        if (error) {
          toastr.error('Upload error', error)
        } else {
          insertToEditor(response.public_id)
        }
      })
    },
    [onUploadingStateChange, insertToEditor],
  )

  const selectLocalImage = useCallback(() => {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.click()
    // Listen upload local image and save to server
    input.onchange = () => {
      const file = input.files[0]
      // file type is only image.
      if (/^image\//.test(file.type)) {
        uploadFileToCloudinary(file)
      } else {
        toastr.warning('You can only upload images.')
      }
    }
  }, [uploadFileToCloudinary])

  useEffect(() => {
    editor.current
      .getEditor()
      .getModule('toolbar')
      .addHandler('image', () => {
        selectLocalImage()
      })
  }, [editor, selectLocalImage])

  const handleChange = useCallback(value => onChange && onChange(value), [onChange])

  const modules = { toolbar, magicUrl: true }
  return (
    <div
      required={isRequired}
      className="input-row"
      css={css`
        align-items: flex-start !important;

        @media (min-width: 768px) {
          label {
            padding-top: 15px !important;
          }
        }
      `}
    >
      {label && <label htmlFor={id}>{label}</label>}
      <div
        css={css`
          flex-grow: 1;
        `}
      >
        <ReactQuill
          theme="snow"
          formats={formats}
          modules={modules}
          value={initialValue || ''}
          placeholder={placeholder}
          onChange={handleChange}
          ref={editor}
        />
      </div>
    </div>
  )
}

RichTextEditor.defaultProps = {
  formats: ['header', 'bold', 'italic', 'blockquote', 'link', 'list', 'image'],
  toolbar: [[{ header: [2, 3, false] }], ['bold', 'italic', 'blockquote', 'link'], [{ list: 'ordered' }, { list: 'bullet' }]],
}

export default connectField(RichTextEditor)
